"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import { useMessengerNotifications } from "@/contexts/MessengerNotificationsContext";

const MAX_DISPLAYED_COUNT = 99;

const stripCounter = (title) => title.replace(/^\(\d+\+?\)\s+/, "");

/**
 * Le nombre de conversations non lues dans le titre de l'onglet.
 *
 * Le titre est réécrit par la couche metadata de l'App Router à chaque
 * navigation : une écriture unique disparaîtrait au premier changement de
 * page. On observe donc l'élément <title> et on repose le préfixe dès qu'il
 * est effacé, en se gardant de réagir à sa propre écriture.
 */
const useUnreadTabTitle = (unreadCount) => {
    const pathname = usePathname();

    useEffect(() => {
        if (typeof document === "undefined") return;

        const apply = () => {
            const current = document.title;
            const base = stripCounter(current);
            const displayed =
                unreadCount > MAX_DISPLAYED_COUNT
                    ? `${MAX_DISPLAYED_COUNT}+`
                    : unreadCount;
            const wanted = unreadCount > 0 ? `(${displayed}) ${base}` : base;
            // Sans cette garde, notre propre écriture relancerait l'observateur.
            if (current !== wanted) document.title = wanted;
        };

        // Le compteur se pose d'abord, l'observateur ensuite : chercher
        // l'élément en premier et sortir s'il manque faisait dépendre l'écriture
        // du titre d'une garde qui ne concerne que la surveillance. On écrit
        // dans document.title, qui crée l'élément au besoin.
        apply();

        const titleElement = document.head?.querySelector("title");
        if (!titleElement) return;

        const observer = new MutationObserver(apply);
        observer.observe(titleElement, { childList: true });
        return () => observer.disconnect();
    }, [unreadCount, pathname]);
};

/**
 * Les notifications du navigateur et le compteur du titre, sur tout le site.
 * Ce composant n'affiche rien.
 *
 * La demande d'autorisation vit dans la liste des conversations du messenger
 * (useNotificationsPrompt) : montée ici, elle s'affichait en bandeau sur
 * toutes les pages, jusque sur l'écran de connexion.
 */
export default function MessengerNotifications() {
    const t = useTranslations();
    const { unreadCount, subscribe, isDiscussionActive } =
        useMessengerNotifications();

    useUnreadTabTitle(unreadCount);

    useEffect(() => {
        const unsubscribe = subscribe(
            "messengerNewMessage",
            ({ discussionId, authorName }) => {
                // Relue à chaque message plutôt qu'au montage : l'autorisation
                // se donne depuis le messenger, et une valeur figée au
                // chargement ignorerait l'accord jusqu'à la page suivante.
                if (
                    typeof Notification === "undefined" ||
                    Notification.permission !== "granted"
                ) {
                    return;
                }
                // Ne pas prévenir de ce qui est déjà à l'écran.
                if (isDiscussionActive(discussionId)) return;
                if (typeof document !== "undefined" && !document.hidden) return;

                try {
                    new Notification(t("messenger_notification_title"), {
                        body: authorName
                            ? t("messenger_notification_body", { name: authorName })
                            : t("messenger_notification_body_generic"),
                        icon: "/static/favicon.ico",
                        // Un onglet leader et ses suiveurs reçoivent tous
                        // l'événement : une étiquette stable fait remplacer la
                        // notification au lieu de l'empiler autant de fois
                        // qu'il y a d'onglets ouverts.
                        tag: `messenger-${discussionId}`,
                    });
                } catch (error) {
                    console.error("Notification error:", error);
                }
            },
        );
        return unsubscribe;
    }, [subscribe, isDiscussionActive, t]);

    return null;
}
